/* widget clearance
 *
 * Collision coordinator for two compact fixed launchers: the Google Merchant
 * widget wrapper (#google-merchantwidget-iframe-wrapper) and the theme chat
 * launcher (#kns-chat-launcher). Each launcher keeps its configured resting
 * bottom margin (vendor/app margin for Google, theme CSS for chat); the
   * coordinator only lifts a launcher, temporarily, above visible purchase
   * controls (quantity buttons/input + primary Add to cart button), selected
   * product identity, price, and gallery counter/arrows when their rectangles
   * actually collide,
 * with a 12px gap. It never hides a launcher, edits the vendor iframe,
 * shrinks/reshapes a control, changes horizontal position, or touches
 * desktop (>768) placement. Unrelated fixed/sticky surfaces are exclusion
 * zones: a launcher never rests or lifts across one.
 */
(function (window, document) {
  "use strict";
  var NAME = "KnsWidgetClearance";
  var previous = window[NAME];
  if (previous && previous.destroy) previous.destroy();

  var GOOGLE = "google-merchantwidget-iframe-wrapper";
  var CHAT = "kns-chat-launcher";
  var COMPACT_MAX = 96, MOBILE_MAX = 768, GAP = 12, HYSTERESIS = 4, RELEASE_FRAMES = 2;

  var api = window[NAME] = {};
  var frame = 0, destroyed = false, ready = false, needsDiscover = false, needsRebind = false;
  var resizeObserver, stateObserver, bodyObserver;
  var bound = [];

  function listen(target, type, fn) {
    if (!target || !target.addEventListener) return;
    target.addEventListener(type, fn, { passive: true });
    bound.push([target, type, fn]);
  }
  function rect(node) { try { return node && node.getBoundingClientRect(); } catch (_) { return null; } }
  function finite(v) { return typeof v === "number" && isFinite(v); }
  function boxOf(left, top, right, bottom) {
    return { left: left, top: top, right: right, bottom: bottom, width: right - left, height: bottom - top };
  }
  // Only the on-screen portion of a partially clipped rect counts for hits.
  function clipToViewport(box) {
    var left = Math.max(0, box.left), right = Math.min(window.innerWidth, box.right);
    var top = Math.max(0, box.top), bottom = Math.min(window.innerHeight, box.bottom);
    if (right <= left || bottom <= top) return null;
    return boxOf(left, top, right, bottom);
  }

  // A node's on-screen, partially-visible rectangle, or null if hidden/detached/zero.
  function usableBox(node) {
    if (!node || !node.isConnected) return null;
    for (var current = node; current && current !== document.documentElement; current = current.parentElement) {
      if (current.hidden || current.getAttribute("aria-hidden") === "true") return null;
      var style = window.getComputedStyle(current);
      if (style.display === "none" || style.visibility === "hidden") return null;
    }
    var box = rect(node);
    if (!box || !finite(box.left) || !finite(box.right) || !finite(box.top) || !finite(box.bottom) || !finite(box.width) || !finite(box.height) || box.width <= 0 || box.height <= 0) return null;
    return clipToViewport(box);
  }
  function visible(node) {
    var box = usableBox(node);
    return !!box && Number(window.getComputedStyle(node).opacity) !== 0;
  }
  function overlaps(a, b) { return a && b && a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top; }
  function fits(box) { return box && finite(box.top) && box.left >= 0 && box.right <= window.innerWidth && box.top >= 0 && box.bottom <= window.innerHeight; }

  // Independent per-launcher node/style ownership.
  function Launcher(id, important) {
    this.id = id;
    this.node = null;
    this.snapshot = null;      // { value, priority } captured at first takeover
    this.owned = false;
    this.vendorChanged = false;
    this.important = !!important;
    this.restBottom = null;    // resting bottom px captured while released
    this.releaseFrames = 0;
    this.compactFrames = 0;
    this._own = "";
  }
  Launcher.prototype.currentIsOurs = function () {
    return this.node && this._own && this.node.style.getPropertyValue("bottom") === this._own &&
      this.node.style.getPropertyPriority("bottom") === (this.important ? "important" : "");
  };
  Launcher.prototype.giveUpIfVendorChanged = function () {
    if (this.owned && !this.currentIsOurs()) { this.owned = false; this.snapshot = null; this._own = ""; this.vendorChanged = true; }
  };
  Launcher.prototype.release = function () {
    if (this.owned && this.currentIsOurs() && this.snapshot) {
      if (this.snapshot.value) this.node.style.setProperty("bottom", this.snapshot.value, this.snapshot.priority);
      else this.node.style.removeProperty("bottom");
    }
    this.owned = false; this.snapshot = null; this._own = "";
  };
  // Geometry-deterministic resting rect, independent of any currently-applied
  // owned offset, so reading it back cannot feed a style-write loop.
  Launcher.prototype.restingRect = function (box) {
    var rest = this.owned ? this.restBottom : this.readRestBottom(box);
    if (!finite(rest) || !box) return null;
    var bottom = window.innerHeight - rest;
    return boxOf(box.left, bottom - box.height, box.left + box.width, bottom);
  };
  Launcher.prototype.readRestBottom = function (box) {
    if (!finite(window.innerHeight) || !box) return null;
    return window.innerHeight - box.bottom;
  };

  var google = new Launcher(GOOGLE, true);
  var chat = new Launcher(CHAT, false);
  var launchers = [google, chat];

  function launcherNodes() {
    var nodes = [];
    for (var i = 0; i < launchers.length; i++) if (launchers[i].node) nodes.push(launchers[i].node);
    return nodes;
  }
  function ownStyleWrite(l, record) {
    return record.target === l.node && record.attributeName === "style" && l.owned && l.currentIsOurs();
  }

  function fixedAncestor(node) {
    for (var current = node; current && current !== document.body; current = current.parentElement) {
      var position = window.getComputedStyle(current).position;
      if (position === "fixed" || position === "sticky") return current;
    }
    return null;
  }

  function protectedNodes() {
    var selectors = [
      "#kns-chat-launcher", "#cw-widget-holder", ".js-overlay", ".sticky-atc-panel", "[data-sticky-atc]", "[data-mobile-bottom-nav]",
      "#cart-drawer", "cart-drawer", "side-drawer", "dialog", "[role=dialog]"
    ];
    var nodes = [];
    for (var i = 0; i < selectors.length; i++) {
      var found = document.querySelectorAll(selectors[i]);
      for (var j = 0; j < found.length; j++) nodes.push(found[j]);
    }
    return nodes;
  }
  function activeSurface(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i], active = node.matches("#cw-widget-holder.woot-widget-holder, .js-overlay.is-visible, #cart-drawer[open], cart-drawer[open], side-drawer[open], #cart-drawer[aria-hidden=false], cart-drawer[aria-hidden=false], side-drawer[aria-hidden=false], dialog[open], [role=dialog]:not([aria-hidden=true])");
      if (active && usableBox(node)) return true;
    }
    return false;
  }

  // First unrelated fixed/sticky surface covering a sample point of box, or
  // null when hit-clear. Our own launchers and their fixed ancestors are never
  // blockers; anything else fixed/sticky (bars, dialogs, unknown chrome) is.
  function firstBlocker(box, ignore) {
    if (!document.elementsFromPoint && !document.elementFromPoint) return null;
    var insetX = Math.min(box.width / 4, 24), insetY = Math.min(box.height / 4, 24);
    var points = [
      [box.left + insetX, box.top + insetY], [box.right - insetX, box.top + insetY],
      [box.left + insetX, box.bottom - insetY], [box.right - insetX, box.bottom - insetY],
      [(box.left + box.right) / 2, (box.top + box.bottom) / 2]
    ];
    for (var p = 0; p < points.length; p++) {
      var x = points[p][0], y = points[p][1];
      if (x < 0 || x > window.innerWidth || y < 0 || y > window.innerHeight) continue;
      var stack = document.elementsFromPoint ? document.elementsFromPoint(x, y) : [document.elementFromPoint(x, y)];
      for (var s = 0; s < stack.length; s++) {
        var target = stack[s];
        if (!target || ignore.indexOf(target) !== -1) continue;
        var fixed = target.nodeType === 1 ? fixedAncestor(target) : null;
        if (!fixed) continue;
        var ours = false;
        for (var f = 0; f < launchers.length; f++) {
          var node = launchers[f].node;
          if (node && (node === fixed || node.contains(fixed) || fixed.contains(node))) { ours = true; break; }
        }
        if (!ours && visible(fixed)) return rect(fixed) || box;
      }
    }
    return null;
  }

  // Visible, enabled ordinary purchase controls in visible product forms only.
  function purchaseControls() {
    var forms = document.querySelectorAll(".js-product-form, product-form form");
    var controls = [];
    for (var f = 0; f < forms.length; f++) {
      var form = forms[f];
      if (!visible(form)) continue;
      var nodes = form.querySelectorAll(".qty-input__btn, .qty-input__input, .qty-input input, .product-info__add-button button, .product-info__add-button .btn");
      for (var n = 0; n < nodes.length; n++) {
        var node = nodes[n];
        if (node.disabled || node.hidden || node.getAttribute("aria-hidden") === "true") continue;
        var box = usableBox(node);
        if (!box) continue;
        if (!containsNode(controls, node)) controls.push({ node: node, box: box });
      }
    }
    // Product identity, price, and gallery controls are outside the form in
    // the live theme. They are geometry obstacles (not hit targets): clearing
    // buttons by covering them is not clear. Protect only these verified
    // selected controls, not every page element or the photo rectangle.
    var obstacleSelectors = [
      ".product-info__title",
      ".product-vendor-sku",
      ".product-info__price",
      "media-gallery .media-ctrl__counter",
      "media-gallery .media-ctrl__btn"
    ];
    for (var s = 0; s < obstacleSelectors.length; s++) {
      var obstacles = document.querySelectorAll(obstacleSelectors[s]);
      for (var o = 0; o < obstacles.length; o++) {
        var obstacle = obstacles[o], obstacleBox = usableBox(obstacle);
        if (obstacleBox && !containsNode(controls, obstacle)) controls.push({ node: obstacle, box: obstacleBox, geometryOnly: true });
      }
    }
    return controls;
  }
  function containsNode(controls, node) {
    for (var i = 0; i < controls.length; i++) if (controls[i].node === node) return true;
    return false;
  }
  function controlNodes(controls) {
    var nodes = [];
    for (var i = 0; i < controls.length; i++) if (nodes.indexOf(controls[i].node) === -1) nodes.push(controls[i].node);
    return nodes;
  }

  function mobileViewportIsUsable() {
    var vv = window.visualViewport;
    return window.innerWidth <= MOBILE_MAX && vv && finite(vv.height) && finite(vv.offsetTop) && finite(vv.scale) && vv.height >= window.innerHeight - 2 && vv.offsetTop === 0 && vv.scale === 1;
  }
  function elementAddedOrRemoved(nodes) {
    for (var i = 0; i < nodes.length; i++) if (nodes[i].nodeType === 1) return true;
    return false;
  }

  // Per-frame placement decision for one launcher (no DOM writes here):
  //  { type: "rest", urgent? }   release / stay released at the configured margin
  //  { type: "nowrite" }         resting rect is collision-free (adopt margin; no write)
  //  { type: "own", bottomPx }   lift so the bottom clears the collision group by GAP
  //  { type: "unresolved" }      no verified in-viewport candidate; never claim clear
  function solve(l, box, otherRect, controls, surfaceActive) {
    var ignore = controlNodes(controls), nodes = launcherNodes(), i;
    for (i = 0; i < nodes.length; i++) if (ignore.indexOf(nodes[i]) === -1) ignore.push(nodes[i]);
    var resting = l.restingRect(box);
    if (!fits(resting)) return { type: "rest" };
    if (surfaceActive) return { type: "rest", urgent: true };

    var restHits = 0, k;
    for (k = 0; k < controls.length; k++) if (overlaps(resting, controls[k].box)) restHits++;
    if (!restHits && !overlaps(resting, otherRect)) return { type: l.owned ? "rest" : "nowrite" };

    var restBottom = window.innerHeight - resting.bottom;
    var bottomPx = restBottom;
    // Every collision move puts the candidate at least GAP above the lowest
    // colliding top edge. A viewport therefore provides a geometry-derived
    // finite bound even when several selected controls or fixed surfaces form
    // a vertical chain. The extra pass verifies the final clear candidate.
    var maxMoves = Math.ceil(window.innerHeight / GAP) + 1;
    for (i = 0; i < maxMoves; i++) {
      var cand = boxOf(box.left, window.innerHeight - bottomPx - box.height, box.left + box.width, window.innerHeight - bottomPx);
      if (!fits(cand)) return { type: "unresolved" };
      var blocker = firstBlocker(cand, ignore);
      var top = null, hits = 0;
      for (k = 0; k < controls.length; k++) if (overlaps(cand, controls[k].box)) { hits++; top = minTop(top, controls[k].box.top); }
      if (blocker && overlaps(cand, blocker)) top = minTop(top, blocker.top);
      if (otherRect && overlaps(cand, otherRect)) top = minTop(top, otherRect.top);
      if (top === null) {
        if (Math.abs(bottomPx - restBottom) < 0.5) return { type: "nowrite" };
        return { type: "own", bottomPx: bottomPx };
      }
      var nextBottomPx = window.innerHeight - (top - GAP);
      // Overlap implies top < cand.bottom, so a valid move must be strictly
      // upward by more than GAP. Refuse non-finite or non-progressing geometry.
      if (!finite(nextBottomPx) || nextBottomPx <= bottomPx + GAP) return { type: "unresolved" };
      bottomPx = nextBottomPx;
    }
    return { type: "unresolved" };
  }
  function minTop(a, b) { return a === null ? b : Math.min(a, b); }

  function apply(l, action) {
    if (action.type === "unresolved") {
      l.releaseFrames = 0;
      if (l.owned) l.release();   // restore vendor/theme rest; never claim clearance
      return;
    }
    if (action.type === "nowrite") {
      if (!l.owned) l.restBottom = l.readRestBottom(rect(l.node));   // adopt live margin as rest
      l.releaseFrames = 0;
      return;
    }
    if (action.type === "rest") {
      if (!l.owned) { l.releaseFrames = 0; return; }
      if (action.urgent) { l.releaseFrames = 0; l.release(); return; }
      l.releaseFrames++;
      if (l.releaseFrames >= RELEASE_FRAMES) { l.release(); l.releaseFrames = 0; }   // 2-frame hysteresis on release
      else schedule();
      return;
    }
    l.releaseFrames = 0;
    if (l.owned) {
      var now = rect(l.node), cur = now ? window.innerHeight - now.bottom : NaN;
      if (finite(cur) && Math.abs(cur - action.bottomPx) < HYSTERESIS) return;        // sub-4px jitter ignored
      l._own = Math.round(action.bottomPx) + "px";
      l.node.style.setProperty("bottom", l._own, l.important ? "important" : "");
      return;
    }
    l.restBottom = l.readRestBottom(rect(l.node));                    // capture rest BEFORE lifting
    l.snapshot = { value: l.node.style.getPropertyValue("bottom"), priority: l.node.style.getPropertyPriority("bottom") };
    l._own = Math.round(action.bottomPx) + "px";
    l.node.style.setProperty("bottom", l._own, l.important ? "important" : "");
    l.owned = true;
  }

  function rebindStateObserver() {
    if (destroyed || !ready) return;
    stateObserver.disconnect();
    for (var i = 0; i < launchers.length; i++) {
      var l = launchers[i];
      if (!l.node) continue;
      stateObserver.observe(l.node, { attributes: true, attributeFilter: ["style", "class", "hidden", "aria-hidden"] });
      if (l.node.parentNode) stateObserver.observe(l.node.parentNode, { childList: true });
    }
    var nodes = protectedNodes();
    for (var j = 0; j < nodes.length; j++) stateObserver.observe(nodes[j], { attributes: true, attributeFilter: ["class", "style", "hidden", "open", "aria-hidden"] });
    var forms = document.querySelectorAll(".js-product-form, product-form form");
    for (var f = 0; f < forms.length; f++) stateObserver.observe(forms[f], { attributes: true, attributeFilter: ["class", "style", "hidden"], childList: true, subtree: true });
    var obstacleSelectors = [
      ".product-info__title",
      ".product-vendor-sku",
      ".product-info__price",
      "media-gallery .media-ctrl__counter",
      "media-gallery .media-ctrl__btn"
    ];
    for (var s = 0; s < obstacleSelectors.length; s++) {
      var obstacles = document.querySelectorAll(obstacleSelectors[s]);
      for (var o = 0; o < obstacles.length; o++) stateObserver.observe(obstacles[o], { attributes: true, attributeFilter: ["class", "style", "hidden", "aria-hidden"] });
    }
  }

  function discover() {
    if (destroyed || !ready) return false;
    var changed = false;
    for (var i = 0; i < launchers.length; i++) {
      var l = launchers[i];
      var next = document.getElementById(l.id);
      if (next === l.node && l.node && l.node.isConnected) continue;
      l.release();
      l.node = next || null;
      l.compactFrames = 0;
      l.vendorChanged = false;
      l.restBottom = null;
      l.releaseFrames = 0;
      if (l.node && resizeObserver) resizeObserver.observe(l.node);
      changed = true;
    }
    if (changed) needsRebind = true;
    return true;
  }

  function evaluate() {
    var usable = mobileViewportIsUsable();
    var controls = usable ? purchaseControls() : [];
    var surface = usable ? activeSurface(protectedNodes()) : false;
    var boxes = [], peers = [];
    for (var i = 0; i < launchers.length; i++) {
      var l = launchers[i];
      boxes.push(null); peers.push(null);
      if (!usable || !l.node || !l.node.isConnected) { l.compactFrames = 0; l.giveUpIfVendorChanged(); l.release(); continue; }
      l.giveUpIfVendorChanged();
      if (l.vendorChanged) continue;
      var raw = rect(l.node), box = usableBox(l.node);
      var closed = raw && box && raw.height > 0 && raw.height <= COMPACT_MAX && (l === google || raw.width <= COMPACT_MAX);
      if (closed) {
        l.compactFrames++;
        boxes[i] = box;
        peers[i] = l.owned ? l.restingRect(box) : box;   // peers judged at rest: no oscillation
      } else l.compactFrames = 0;
    }
    if (!usable) return;
    api.state = { unresolved: [] };
    // Deterministic priority prevents two launchers from leapfrogging each
    // other: solve Google first, then solve chat against Google's result.
    var googleAction = boxes[0] ? solve(google, boxes[0], peers[1], controls, surface) : { type: "rest" };
    var googleRect = boxes[0] ? actionRect(google, boxes[0], googleAction) : null;
    var chatAction = boxes[1] ? solve(chat, boxes[1], googleRect, controls, surface) : { type: "rest" };
    if (googleAction.type === "unresolved") api.state.unresolved.push(GOOGLE);
    if (chatAction.type === "unresolved") api.state.unresolved.push(CHAT);
    if (google.node && !google.vendorChanged) apply(google, googleAction);
    if (chat.node && !chat.vendorChanged) apply(chat, chatAction);
  }
  function actionRect(l, box, action) {
    if (action.type === "own") return boxOf(box.left, window.innerHeight - action.bottomPx - box.height, box.right, window.innerHeight - action.bottomPx);
    return l.restingRect(box);
  }

  function flush() {
    frame = 0;
    if (destroyed || !ready) return;
    if (needsDiscover) { needsDiscover = false; discover(); }
    if (needsRebind) { needsRebind = false; rebindStateObserver(); }
    evaluate();
  }
  function request(discoverToo, rebindToo) {
    if (destroyed || !ready) return;
    if (discoverToo) needsDiscover = true;
    if (rebindToo) needsRebind = true;
    if (!frame) frame = window.requestAnimationFrame(flush);
  }
  function schedule() { request(false, false); }

  api.refresh = function () { request(true, true); };
  api.destroy = function () {
    if (destroyed) return;
    destroyed = true; ready = false;
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0; needsDiscover = needsRebind = false;
    for (var i = 0; i < launchers.length; i++) launchers[i].release();
    if (resizeObserver) resizeObserver.disconnect();
    if (stateObserver) stateObserver.disconnect();
    if (bodyObserver) bodyObserver.disconnect();
    for (var b = 0; b < bound.length; b++) bound[b][0].removeEventListener(bound[b][1], bound[b][2]);
    bound = [];
  };

  function init() {
    if (destroyed || !document.body || !window.ResizeObserver || !window.MutationObserver || !window.visualViewport || !window.requestAnimationFrame) return;
    resizeObserver = new window.ResizeObserver(schedule);
    stateObserver = new window.MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var own = false;
        for (var k = 0; k < launchers.length; k++) if (ownStyleWrite(launchers[k], records[i])) { own = true; break; }
        if (!own) {
          if (records[i].attributeName === "style") {
            for (var m = 0; m < launchers.length; m++) if (records[i].target === launchers[m].node) launchers[m].vendorChanged = false;
          }
          request(false, true);
          return;
        }
      }
    });
    bodyObserver = new window.MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (elementAddedOrRemoved(records[i].addedNodes) || elementAddedOrRemoved(records[i].removedNodes)) { request(true, true); return; }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    ready = true;
    listen(window, "resize", schedule);
    listen(window, "scroll", schedule);
    listen(window.visualViewport, "resize", schedule);
    listen(window.visualViewport, "scroll", schedule);
    request(true, true);
  }
  if (document.body) init(); else document.addEventListener("DOMContentLoaded", init, { once: true });
})(window, document);
